import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { FleetVehicle, FleetStatus } from '../../mock/models/fleet.models';
import { Workshop } from '../../mock/models/workshop.model';
import { WorkshopMockService } from '../../mock/workshop-mock.service';
import { MapOptionsConstants } from '../../../../shared/constants/common.constants';

/** Color semáforo por estado de telemetría. */
const STATUS_COLOR: Record<FleetStatus, string> = {
  MOVING: '#10b981',
  STOPPED: '#ef4444',
  IDLE: '#f59e0b',
};

/** Emoji por categoría de taller (fallback 🔧). */
const CATEGORY_ICON: Record<string, string> = {
  'Mecánica general autos y camionetas': '🚘',
  'Taller de Motos': '🏍️',
  'Taller de Latonería y Pintura': '🎨',
  'Taller de Vehículos Pesados': '🚛',
  Autolavado: '🧼',
  'Taller de Scooters': '🛵',
};

interface CategoryChip {
  name: string;
  icon: string;
  count: number;
}

@Component({
  selector: 'app-live-map-widget',
  templateUrl: './live-map-widget.component.html',
  styleUrl: './live-map-widget.component.css',
})
export class LiveMapWidgetComponent implements OnInit {
  @ViewChild(MapInfoWindow, { static: false }) infoWindow?: MapInfoWindow;

  @Input() vehicles: FleetVehicle[] = [];
  @Input() selectedId: string | null = null;
  @Output() vehicleSelected = new EventEmitter<FleetVehicle>();

  readonly mapOptions = MapOptionsConstants;
  /** Acota el ancho del InfoWindow para que nunca se infle (bug del recuadro gris gigante). */
  readonly infoOptions: google.maps.InfoWindowOptions = { maxWidth: 260 };
  /** Centro por defecto: Colombia. */
  readonly defaultCenter: google.maps.LatLngLiteral = { lat: 4.6, lng: -74.1 };
  readonly defaultZoom = 6;

  // Info window compartido: alterna entre vehículo y taller.
  infoMode: 'vehicle' | 'workshop' = 'vehicle';
  activeVehicle: FleetVehicle | null = null;
  activeWorkshop: Workshop | null = null;
  imgError = false; // la foto del taller no cargó → se oculta

  // Capa de talleres.
  workshops: Workshop[] = [];
  showWorkshops = true;
  categories: CategoryChip[] = [];
  activeCategories = new Set<string>(); // vacío = todas
  loadingWorkshops = true;

  /** Un único ícono reutilizado para los 475 marcadores (el navegador cachea el data-URI). */
  readonly workshopIcon = this.buildWorkshopIcon();

  constructor(private readonly workshopService: WorkshopMockService) {
    // do nothing
  }

  ngOnInit(): void {
    this.workshopService.getWorkshops().subscribe((list) => {
      this.workshops = list;
      this.categories = this.buildCategoryChips(list);
      this.loadingWorkshops = false;
    });
  }

  // ---------- Vehículos ----------

  /** Centra en el vehículo seleccionado si existe; si no, en Colombia. */
  get center(): google.maps.LatLngLiteral {
    const sel = this.vehicles.find((v) => v.id === this.selectedId);
    return sel ? { lat: sel.telemetry.lat, lng: sel.telemetry.lng } : this.defaultCenter;
  }

  get zoom(): number {
    return this.selectedId ? 13 : this.defaultZoom;
  }

  position(v: FleetVehicle): google.maps.LatLngLiteral {
    return { lat: v.telemetry.lat, lng: v.telemetry.lng };
  }

  /** Ícono circular coloreado por estado (data-URI SVG). El seleccionado va más grande. */
  iconFor(v: FleetVehicle): any {
    const color = STATUS_COLOR[v.status_live];
    const selected = v.id === this.selectedId;
    const size = selected ? 30 : 22;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
      <circle cx="15" cy="15" r="12" fill="${color}" stroke="#ffffff" stroke-width="3"/>
    </svg>`;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: { width: size, height: size },
      anchor: { x: size / 2, y: size / 2 } as any,
    };
  }

  onMarkerClick(marker: MapMarker, v: FleetVehicle): void {
    this.infoMode = 'vehicle';
    this.activeVehicle = v;
    this.activeWorkshop = null;
    this.vehicleSelected.emit(v);
    this.openInfo(marker);
  }

  trackById(_: number, v: FleetVehicle): string {
    return v.id;
  }

  // ---------- Talleres ----------

  /** Talleres visibles según toggle + filtro de categoría. */
  get visibleWorkshops(): Workshop[] {
    if (!this.showWorkshops) return [];
    if (this.activeCategories.size === 0) return this.workshops;
    return this.workshops.filter((w) => w.categories.some((c) => this.activeCategories.has(c)));
  }

  toggleWorkshops(): void {
    this.showWorkshops = !this.showWorkshops;
  }

  toggleCategory(name: string): void {
    if (this.activeCategories.has(name)) this.activeCategories.delete(name);
    else this.activeCategories.add(name);
    // Nuevo Set para disparar detección de cambios en el getter.
    this.activeCategories = new Set(this.activeCategories);
  }

  clearCategories(): void {
    this.activeCategories = new Set<string>();
  }

  isCategoryActive(name: string): boolean {
    return this.activeCategories.size === 0 || this.activeCategories.has(name);
  }

  workshopPosition(w: Workshop): google.maps.LatLngLiteral {
    return { lat: w.lat, lng: w.lng };
  }

  onWorkshopClick(marker: MapMarker, w: Workshop): void {
    this.infoMode = 'workshop';
    this.activeWorkshop = w;
    this.activeVehicle = null;
    this.imgError = false;
    this.openInfo(marker);
  }

  /**
   * Abre el InfoWindow DIFERIDO: espera a que Angular proyecte el contenido
   * *ngIf antes de que Google Maps mida el tamaño. Evita el recuadro gris
   * vacío/gigante que tapaba el mapa.
   */
  private openInfo(marker: MapMarker): void {
    setTimeout(() => this.infoWindow?.open(marker));
  }

  categoryEmoji(name: string): string {
    return CATEGORY_ICON[name] ?? '🔧';
  }

  trackByWorkshop(_: number, w: Workshop): string {
    return w.id;
  }

  private buildWorkshopIcon(): any {
    // Pin tipo gota (mismo del vehículo) en naranja para distinguir los talleres.
    const color = '#f97316';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
      <path d="M20 1C10 1 2 9 2 19c0 13 18 32 18 32s18-19 18-32C38 9 30 1 20 1Z"
        fill="${color}" stroke="#ffffff" stroke-width="2.5"/>
      <circle cx="20" cy="19" r="8" fill="#ffffff"/>
      <circle cx="20" cy="19" r="4" fill="${color}"/>
    </svg>`;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: { width: 30, height: 39 },
      anchor: { x: 15, y: 39 } as any,
    };
  }

  private buildCategoryChips(list: Workshop[]): CategoryChip[] {
    const counts = new Map<string, number>();
    for (const w of list) {
      for (const c of w.categories) counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count, icon: CATEGORY_ICON[name] ?? '🔧' })).sort((a, b) => b.count - a.count);
  }
}
